import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { TrendingUp, Users, Calendar, Euro, Download, AlertCircle, Trophy } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const { getAuthHeaders } = useAuth();
  const [stats, setStats] = useState(null);
  const [advancedStats, setAdvancedStats] = useState(null);
  const [revenueChart, setRevenueChart] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadDemoData = async () => {
    try {
      await axios.post(`${API_URL}/api/demo/populate`, {}, getAuthHeaders());
      toast.success('Données de démonstration ajoutées');
      fetchStats();
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    }
  };

  const fetchStats = async () => {
    try {
      const [statsRes, advancedRes, chartRes] = await Promise.all([
        axios.get(`${API_URL}/api/dashboard/stats`, getAuthHeaders()),
        axios.get(`${API_URL}/api/dashboard/advanced-stats`, getAuthHeaders()),
        axios.get(`${API_URL}/api/dashboard/revenue-chart`, getAuthHeaders()),
      ]);
      setStats(statsRes.data);
      setAdvancedStats(advancedRes.data);
      setRevenueChart(chartRes.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center h-96">Chargement...</div>;
  }

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Tableau de bord</h1>
          <p className="text-muted-foreground mt-2">Vue d&apos;ensemble de votre activité</p>
        </div>
        {stats?.clients_count === 0 && (
          <Button onClick={loadDemoData} data-testid="btn-load-demo" className="w-full sm:w-auto">
            <Download className="w-4 h-4 mr-2" />
            Charger données de démo
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <Card className="rounded-xl border-border/60 bg-gradient-to-br from-rose-50 to-orange-50" data-testid="stat-month-revenue">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">CA du mois</CardTitle>
            <TrendingUp className="h-5 w-5 text-rose-600" strokeWidth={1.5} />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">{stats?.month_revenue?.toFixed(2) || '0.00'} €</div>
            <p className="text-xs text-gray-500 mt-1">
              Ma part: {stats?.month_my_share?.toFixed(2) || '0.00'} €
            </p>
          </CardContent>
        </Card>

        <Card className="rounded-xl border-border/60 bg-gradient-to-br from-blue-50 to-cyan-50" data-testid="stat-year-revenue">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">CA annuel</CardTitle>
            <Euro className="h-5 w-5 text-blue-600" strokeWidth={1.5} />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">{stats?.year_revenue?.toFixed(2) || '0.00'} €</div>
            <p className="text-xs text-gray-500 mt-1">
              Ma part: {stats?.year_my_share?.toFixed(2) || '0.00'} €
            </p>
          </CardContent>
        </Card>

        <Card className="rounded-xl border-border/60" data-testid="stat-clients">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Client·e·s</CardTitle>
            <Users className="h-5 w-5 text-purple-600" strokeWidth={1.5} />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats?.clients_count || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">Total client·e·s</p>
          </CardContent>
        </Card>

        <Card className="rounded-xl border-border/60" data-testid="stat-appointments">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Rendez-vous</CardTitle>
            <Calendar className="h-5 w-5 text-green-600" strokeWidth={1.5} />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats?.appointments_count || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">Total rendez-vous</p>
          </CardContent>
        </Card>
      </div>

      {advancedStats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          <Card className="rounded-xl border-border/60 bg-gradient-to-br from-amber-50 to-yellow-50">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-2">
                <Trophy className="w-5 h-5 text-amber-600" />
                <p className="text-sm text-gray-600">CA moyen / RDV</p>
              </div>
              <p className="text-3xl font-bold text-gray-900">{advancedStats.avg_revenue_per_appointment.toFixed(2)} €</p>
            </CardContent>
          </Card>
          
          <Card className="rounded-xl border-border/60 bg-gradient-to-br from-red-50 to-pink-50">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-2">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <p className="text-sm text-gray-600">Taux No-Show</p>
              </div>
              <p className="text-3xl font-bold text-gray-900">{advancedStats.no_show_rate}%</p>
              <p className="text-xs text-gray-500 mt-1">{advancedStats.total_no_shows} no-shows</p>
            </CardContent>
          </Card>

          <Card className="rounded-xl border-border/60 bg-gradient-to-br from-green-50 to-emerald-50">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-2">
                <Trophy className="w-5 h-5 text-green-600" />
                <p className="text-sm text-gray-600">Prestation populaire</p>
              </div>
              {advancedStats.popular_services.length > 0 ? (
                <>
                  <p className="text-xl font-bold text-gray-900">{advancedStats.popular_services[0].nom}</p>
                  <p className="text-xs text-gray-500 mt-1">{advancedStats.popular_services[0].count} fois</p>
                </>
              ) : (
                <p className="text-sm text-gray-500">Aucune donnée</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {revenueChart.length > 0 && (
        <Card className="rounded-xl border-border/60" data-testid="revenue-chart">
          <CardHeader>
            <CardTitle className="text-lg sm:text-xl">Évolution du chiffre d&apos;affaires</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={revenueChart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="month" stroke="#6b7280" style={{ fontSize: '10px' }} />
                <YAxis stroke="#6b7280" style={{ fontSize: '10px' }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="revenue" 
                  stroke="#FF6B6B" 
                  strokeWidth={2}
                  dot={{ fill: '#FF6B6B', r: 3 }}
                  name="CA (€)"
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {advancedStats?.popular_services.length > 0 && (
        <Card className="rounded-xl border-border/60" data-testid="popular-services-chart">
          <CardHeader>
            <CardTitle className="text-lg sm:text-xl">Prestations les plus demandées</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={advancedStats.popular_services}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="nom" stroke="#6b7280" style={{ fontSize: '10px' }} />
                <YAxis stroke="#6b7280" style={{ fontSize: '10px' }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                />
                <Bar dataKey="count" fill="#4ECDC4" radius={[6, 6, 0, 0]} name="Réservations" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {stats?.upcoming_appointments?.length > 0 && (
        <Card className="rounded-xl border-border/60" data-testid="upcoming-appointments">
          <CardHeader>
            <CardTitle>Prochains rendez-vous</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {stats.upcoming_appointments.map((apt) => {
                const aptDate = new Date(apt.date);
                const now = new Date();
                const minutesUntil = Math.floor((aptDate - now) / (1000 * 60));
                const hoursUntil = Math.floor(minutesUntil / 60);
                const remainingMinutes = minutesUntil % 60;
                const isUrgent = minutesUntil < 24 * 60 && minutesUntil > 0;
                
                let timeText = '';
                if (minutesUntil < 0) {
                  timeText = 'Passé';
                } else if (minutesUntil < 60) {
                  timeText = `Dans ${minutesUntil}min`;
                } else if (hoursUntil < 24) {
                  timeText = `Dans ${hoursUntil}h${remainingMinutes > 0 ? remainingMinutes + 'min' : ''}`;
                } else {
                  const days = Math.floor(hoursUntil / 24);
                  timeText = `Dans ${days} jour${days > 1 ? 's' : ''}`;
                }
                
                return (
                  <div
                    key={apt.id}
                    className={`flex items-center justify-between p-4 rounded-lg border ${
                      isUrgent ? 'bg-amber-50 border-amber-200' : 'bg-muted/30 border-border/40'
                    }`}
                    data-testid={`upcoming-appointment-${apt.id}`}
                  >
                    <div className="flex items-center gap-3">
                      {isUrgent && <AlertCircle className="w-5 h-5 text-amber-600" />}
                      <div>
                        <p className="font-medium">{apt.client_name}</p>
                        <p className="text-sm text-muted-foreground">
                          {format(aptDate, 'PPP à HH:mm', { locale: fr })}
                        </p>
                        <p className={`text-xs font-medium mt-1 ${isUrgent ? 'text-amber-600' : 'text-muted-foreground'}`}>
                          {timeText}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold">{apt.prix.toFixed(2)} €</p>
                      <p className="text-xs text-muted-foreground">{apt.statut}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}